import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Container, Reveal, SectionLabel } from "../components/primitives";
import Seo from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const plans = [
  {
    key: "starter_onetime", name: "Starter", price: 497, cadence: "one-time",
    tagline: "A clean, professional presence to get found.",
    features: ["Up to 3 pages", "Mobile responsive", "Contact form", "Basic SEO", "1 revision"],
  },
  {
    key: "professional_onetime", name: "Professional", price: 997, cadence: "one-time", popular: true,
    tagline: "Everything a growing business needs to convert.",
    features: ["Up to 7 pages", "Premium design", "Mobile responsive", "Contact/quote form", "SEO setup", "Google Maps", "Testimonials", "Gallery", "2 revisions", "72-hour delivery"],
  },
  {
    key: "premium_onetime", name: "Premium", price: 1497, cadence: "one-time",
    tagline: "A high-converting flagship site with lead-gen built in.",
    features: ["Up to 12 pages", "Custom premium design", "Advanced forms", "Lead-generation features", "SEO setup", "Analytics", "Gallery", "Testimonials", "Blog", "3 revisions", "72-hour delivery"],
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);

  const checkout = async (lookup_key) => {
    if (!user) {
      localStorage.setItem("wls_intended_plan", lookup_key);
      toast.info("Create an account to continue to checkout");
      navigate("/register");
      return;
    }
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  return (
    <div data-testid="pricing-page" className="pt-32">
      <Seo page="pricing" />
      <Container>
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel>Pricing</SectionLabel>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F3EDDC]">Simple, transparent pricing</h1>
            <p className="mt-5 text-lg text-[#B7AF98]">Pick a package and we'll get to work. No hidden fees, ever.</p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {plans.map((p, i) => (
            <Reveal key={p.key} delay={i * 70}>
              <div
                data-testid={`plan-${p.name.toLowerCase()}`}
                className={`relative rounded-3xl border bg-[#141414] p-8 h-full flex flex-col ${
                  p.popular ? "border-[#C9A227] shadow-[0_20px_50px_rgba(201,162,39,0.15)] lg:-mt-4 lg:mb-4" : "border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-[#C9A227] px-4 py-1 text-xs font-semibold text-white">
                    <Sparkles className="h-3 w-3" /> Most Popular
                  </span>
                )}
                <h3 className="font-display text-xl font-bold text-[#F3EDDC]">{p.name}</h3>
                <p className="mt-1 text-sm text-[#948C79] min-h-[40px]">{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold text-[#F3EDDC]">${p.price}</span>
                  <span className="text-[#948C79]">/{p.cadence}</span>
                </div>
                <Button
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  onClick={() => checkout(p.key)}
                  disabled={loadingKey === p.key}
                  className={`mt-6 w-full rounded-full h-12 ${p.popular ? "gold-shimmer-btn bg-[#C9A227] hover:bg-[#B0891C] text-black" : "bg-black hover:bg-[#242424] text-white"}`}
                >
                  {loadingKey === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get Started"}
                </Button>
                <ul className="mt-8 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-[#D2CAB2]">
                      <Check className="h-5 w-5 text-[#C9A227] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Website Refresh */}
        <Reveal>
          <div data-testid="plan-refresh" className="mt-10 rounded-3xl border border-[#C9A227]/30 bg-[#141414] p-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#C9A227]/10 text-[#C9A227] px-3 py-1 text-xs font-medium">Already have a website?</div>
              <h3 className="mt-4 font-display text-2xl font-bold text-[#F3EDDC]">Website Refresh — $697</h3>
              <p className="mt-2 text-[#B7AF98]">Give your existing website a professional redesign and update — modern design, mobile optimization, refreshed content and SEO — without starting from scratch.</p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#D2CAB2]">
                {["Full redesign", "Mobile optimization", "Content updates", "SEO refresh", "2 revisions"].map((f) => (
                  <span key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C9A227]" /> {f}</span>
                ))}
              </div>
            </div>
            <Button
              data-testid="plan-cta-refresh"
              onClick={() => checkout("refresh_onetime")}
              disabled={loadingKey === "refresh_onetime"}
              className="gold-shimmer-btn rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12 px-8 shrink-0"
            >
              {loadingKey === "refresh_onetime" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get a Refresh $697"}
            </Button>
          </div>
        </Reveal>

        <p className="mt-8 mb-24 text-center text-sm text-[#7E7663]">
          Every package is a one-time purchase — no subscriptions, no monthly fees. Payments are securely processed by Stripe. We never store your card details.
        </p>
      </Container>
    </div>
  );
}
